import { Box, Flex, Text } from '@chakra-ui/layout';
import { useEffect, useMemo, useState } from 'react';
import { useTransactions } from 'store/transactions/useTransactions';
import { Crucible, ERC20Token } from 'types';
import { useSelectedAssets } from 'store/selectedAssets';
import { useTokenBlacklist } from 'hooks/query/useTokenBlacklist';
import { TxnStatus, TxnType } from 'store/transactions/types';
import { useWeb3React } from 'hooks/web3';
import { useConfig } from 'store/config';
import { parseUnits } from 'ethers/lib/utils';
import { Checkbox } from '@chakra-ui/react';
import AssetSelector from './assetSelector';
import TokenTransferForm from './tokenTransferForm';
import AssetsLoadingSkeleton from './AssetsLoadingSkeleton';
import IUniswapV2ERC20 from '@uniswap/v2-core/build/IUniswapV2ERC20.json';
import { ethers } from 'ethers';

type Props = {
  crucible: Crucible;
  walletAssets: ERC20Token[] | undefined;
  assetsStatus: 'idle' | 'error' | 'loading' | 'success';
  assetsFetching: boolean;
};

const TokenWrapper: React.FC<Props> = ({
  crucible,
  walletAssets,
  assetsStatus,
  assetsFetching
}) => {
  const { handleWrapping } = useTransactions();
  const { chainId = 1, account, library } = useWeb3React();
  const { data: tokenBlacklist = [] } = useTokenBlacklist();
  const { selectedWalletAsset, setSelectedWalletAsset } = useSelectedAssets();
  const { wrappableTokens } = useConfig(chainId).config;
  const { transactions } = useTransactions();

  const [amount, setAmount] = useState('');
  const [autoDeposit, setAutoDeposit] = useState(true);
  const [approved, setApproved] = useState(false);

  const wrappableTokenAddresses = wrappableTokens!.flatMap((token) => [
    token.underlyingTokenAddress.toLowerCase(),
    token.wrapperTokenAddress.toLowerCase()
  ]);

  const assetSelector = useMemo(() => {
    return (
      walletAssets &&
      wrappableTokenAddresses.some((tokenAdress) =>
        walletAssets
          .map((asset) => asset.address.toLowerCase())
          .includes(tokenAdress.toLowerCase())
      ) && (
        <AssetSelector
          selectedAsset={selectedWalletAsset}
          assets={walletAssets}
          setSelectedAsset={setSelectedWalletAsset}
          tokenBlacklist={tokenBlacklist}
        />
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWalletAsset, walletAssets]);

  const { underlyingToken, wrapperToken, isWrap, supportsAutoDeposit } =
    useMemo(() => {
      if (selectedWalletAsset) {
        const matchingObject = wrappableTokens!.find(
          (token) =>
            token.wrapperTokenAddress.toLowerCase() ===
              selectedWalletAsset.address.toLowerCase() ||
            token.underlyingTokenAddress.toLowerCase() ===
              selectedWalletAsset.address.toLowerCase()
        );

        if (!matchingObject) {
          return {
            underlyingToken: null,
            wrapperToken: null,
            isWrap: null
          };
        }

        let isWrap = false;

        if (
          selectedWalletAsset.address.toLowerCase() ===
          matchingObject.underlyingTokenAddress.toLowerCase()
        ) {
          isWrap = true;
        }

        const underlyingToken = {
          underlyingTokenAddress: matchingObject.underlyingTokenAddress,
          underlyingTokenSymbol: matchingObject.underlyingTokenSymbol
        };

        const wrapperToken = {
          wrapperTokenAddress: matchingObject.wrapperTokenAddress,
          wrapperTokenSymbol: matchingObject.wrapperTokenSymbol
        };

        const supportsAutoDeposit = matchingObject.supportsAutoDeposit;

        return { underlyingToken, wrapperToken, isWrap, supportsAutoDeposit };
      } else {
        return {
          underlyingToken: null,
          wrapperToken: null,
          isWrap: null
        };
      }
    }, [selectedWalletAsset, wrappableTokens]);

  const checkAllowance = async () => {
    if (wrapperToken && underlyingToken && selectedWalletAsset && isWrap) {
      const underlying = new ethers.Contract(
        underlyingToken.underlyingTokenAddress,
        IUniswapV2ERC20.abi,
        library.getSigner()
      );

      const allowance = await underlying.allowance(
        account,
        wrapperToken!.wrapperTokenAddress
      );

      if (
        allowance.lt(
          parseUnits(amount || '0', selectedWalletAsset.decimals || 18)
        )
      ) {
        setApproved(false);
      } else {
        setApproved(true);
      }
    }
  };

  useEffect(() => {
    if (
      selectedWalletAsset &&
      !wrappableTokenAddresses.includes(
        selectedWalletAsset.address.toLowerCase()
      )
    ) {
      if (walletAssets && walletAssets.length > 0) {
        setSelectedWalletAsset(walletAssets[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrappableTokenAddresses, walletAssets, selectedWalletAsset]);

  useEffect(() => {
    if (
      selectedWalletAsset &&
      wrappableTokenAddresses.includes(
        selectedWalletAsset.address.toLocaleLowerCase()
      )
    ) {
      checkAllowance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrappableTokenAddresses, walletAssets, selectedWalletAsset]);

  const transacting = useMemo(() => {
    return !!transactions.find(
      (transaction) =>
        transaction.type === TxnType.handleWrapping &&
        transaction.account === account &&
        transaction.status === TxnStatus.PendingOnChain
    );
  }, [transactions, account]);

  const buttonText = useMemo(() => {
    if (!wrapperToken || !underlyingToken || !selectedWalletAsset) {
      return 'Wrapping unavailable';
    }

    if (transacting) {
      return 'Pending...';
    }

    if (isWrap) {
      return `Wrap ${underlyingToken.underlyingTokenSymbol} to ${wrapperToken.wrapperTokenSymbol}`;
    } else {
      return `Unwrap ${wrapperToken.wrapperTokenSymbol} to ${underlyingToken.underlyingTokenSymbol}`;
    }
  }, [isWrap, underlyingToken, wrapperToken, selectedWalletAsset, transacting]);

  if (assetsStatus === 'error') {
    return (
      <Flex p={[4, 6]} borderRadius='xl' bg='gray.700'>
        <Text textAlign='center'>
          Failed to get available assets in your wallet. Please try refreshing
          the page.
        </Text>
      </Flex>
    );
  }

  if (assetsStatus === 'loading' || assetsStatus === 'idle' || assetsFetching) {
    return <AssetsLoadingSkeleton />;
  }

  if (!selectedWalletAsset || !walletAssets) {
    return (
      <Flex p={[4, 6]} borderRadius='xl' bg='gray.700'>
        <Text textAlign='center'>
          There are no assets available to wrap/unwrap
        </Text>
      </Flex>
    );
  }

  if (wrappableTokens && wrappableTokens.length === 0) {
    return (
      <Text>You do not have any tokens which can be wrapped/unwrapped</Text>
    );
  }

  const tokenBalance = selectedWalletAsset?.value.amount;
  const tokenDecimals = selectedWalletAsset?.decimals || 18;

  return (
    <>
      {supportsAutoDeposit && isWrap && (
        <Box display='flex'>
          <Checkbox
            defaultChecked={autoDeposit}
            onChange={(e) => setAutoDeposit(e.target.checked)}
            mb={3}
            ml='auto'
          >
            Auto-deposit to this Crucible
          </Checkbox>
        </Box>
      )}
      <TokenTransferForm
        isDisabled={!underlyingToken || !wrapperToken || transacting}
        tokenBalance={tokenBalance}
        tokenDecimals={tokenDecimals}
        selectedAsset={selectedWalletAsset}
        amount={amount}
        setAmount={setAmount}
        assetSelector={assetSelector || undefined}
        handleTransfer={() =>
          handleWrapping(
            crucible.id,
            wrapperToken!,
            underlyingToken!,
            account as string,
            parseUnits(amount, selectedWalletAsset.decimals || 18),
            isWrap!,
            autoDeposit
          )
        }
        submitButtonText={approved ? buttonText : 'Approve and ' + buttonText}
      />
    </>
  );
};

export default TokenWrapper;
